import {
  Bar,
  ComposedChart,
  LabelList,
  Line,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { TableCell, TableRow } from "@/components/ui/table";
import { downloadCsv, toCsv } from "@/lib/utils/csv";
import { useDashboardUrlState } from "./useDashboardUrlState";
import { referralPeriods, periodLabel } from "./referral-periods";
import {
  DashboardTable,
  useDashboardTable,
  type DashboardTableColumn,
} from "./dashboard-table";

export type DemographicRow = {
  period: string;
  role: "referrer" | "referred";
  dimension: string;
  segment: string;
  customers: number;
};

export function referralCategoryColor(dimension: string, segment: string) {
  if (segment === "Ukjent") return "var(--color-admin-text-subtle)";
  const categories = dimension === "age" ? AGE_GROUPS : ["Kvinne", "Mann"];
  const index = Math.max(0, categories.indexOf(segment));
  // Hash the tier itself: dates, filters and failed option requests cannot change
  // its color. Known age/gender categories use spaced golden-angle hues.
  let hash = 2166136261;
  for (const char of segment) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  const hue = dimension === "product" ? (hash % 360000) / 1000 : (index * 137.508 + 25) % 360;
  return `oklch(from var(--color-admin-info-400) l c ${hue})`;
}
export const AGE_GROUPS = [
  "Under 30",
  "30–39",
  "40–49",
  "50–59",
  "60–69",
  "70+",
  "Ukjent",
];
export const REFERRAL_DIMENSIONS = {
  age: "Aldersgruppe",
  gender: "Kjønn",
  product: "Abonnementstype",
};

function referralSegments(rows: DemographicRow[]) {
  return [...new Set(rows.map((r) => r.segment))].sort((a, b) => {
    if (a === "Ukjent") return 1;
    if (b === "Ukjent") return -1;
    return rows[0]?.dimension === "age"
      ? AGE_GROUPS.indexOf(a) - AGE_GROUPS.indexOf(b)
      : a.localeCompare(b, "nb");
  });
}

export function referralChartData(
  rows: DemographicRow[],
  from: string,
  to: string,
  granularity: string,
  percent: boolean,
  segments = referralSegments(rows),
) {
  const data = referralPeriods(from, to, granularity).map((period) => {
    const values = new Map(rows.filter((r) => r.period === period).map((r) => [r.segment, Number(r.customers)]));
    const total = [...values.values()].reduce((sum, value) => sum + value, 0);
    const point: Record<string, string | number> = { period, total };
    segments.forEach((segment, i) => {
      const count = values.get(segment) ?? 0;
      point[`s${i}`] = percent ? (total ? count / total * 100 : 0) : count;
    });
    return point;
  });
  return { segments, data };
}

export function ReferralDemographics({
  rows,
  from,
  to,
  granularity,
}: {
  rows: DemographicRow[];
  from: string;
  to: string;
  granularity: string;
}) {
  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <h2 className="mr-auto text-lg font-semibold">
          Hvem verver og hvem blir vervet?
        </h2>
      </div>
      <p className="text-xs text-admin-text-subtle">
        Unike personer per {periodLabel(granularity).toLocaleLowerCase("nb-NO")}, ikke
        antall vervinger. Manglende data vises som Ukjent.
        Tall over søylene viser antall personer; n viser antallet bak prosentfordelingen.
      </p>
      <details className="text-xs text-admin-text-subtle">
        <summary className="cursor-pointer">
          Definisjoner og datagrunnlag
        </summary>
        <p className="mt-2 leading-relaxed">
          En verver med flere vervinger telles én gang per periode, ved
          periodens første verving. Samme verver kan telles i flere perioder.
          VERVET-kode uten kjent verver er ikke inkludert. Alder beregnes ved
          vervingen fra nyeste profil; kjønn er sist registrerte verdi.
          Abonnementstype er sist synkroniserte type på det sist startede
          betalte abonnementet frem til vervingen (også avsluttede
          abonnementer). Dette er ikke en full historikk over aktive
          abonnementer eller typeendringer. Manglende data vises som Ukjent.
          Andeler beregnes separat for hver rolle og periode, inkludert Ukjent.
        </p>
      </details>
      <div className="grid gap-4 xl:grid-cols-2">
        {(["referrer", "referred"] as const).map((role) => (
          <DemographicPanel
            key={role}
            {...{
              from,
              to,
              granularity,
              role,
              rows,
            }}
          />
        ))}
      </div>
    </section>
  );
}

function DemographicPanel({
  rows: allRows,
  from,
  to,
  granularity,
  role,
}: {
  rows: DemographicRow[];
  from: string;
  to: string;
  granularity: string;
  role: string;
}) {
  const dimensionKey = `${role}Dimension`;
  const viewKey = `${role}View`;
  const modeKey = `${role}Mode`;
  // The page hydrates all per-chart defaults in a single URL update.
  const [state, update] = useDashboardUrlState({ [dimensionKey]: "", [viewKey]: "", [modeKey]: "" });
  const requestedDimension = state[dimensionKey] || "age";
  const dimension = Object.hasOwn(REFERRAL_DIMENSIONS, requestedDimension) ? requestedDimension : "age";
  const view = state[viewKey] || "absolute";
  const mode = state[modeKey] || "chart";
  const rows = allRows.filter((r) => r.role === role && r.dimension === dimension);
  // Identical colors across roles, including groups absent on one side.
  const segments = referralSegments(allRows.filter((r) => r.dimension === dimension));
  const percent = view === "percent";
  const { data } = referralChartData(
    rows,
    from,
    to,
    granularity,
    percent,
    segments,
  );
  const totals = new Map<string, number>();
  rows.forEach((r) =>
    totals.set(r.period, (totals.get(r.period) ?? 0) + Number(r.customers)),
  );
  const share = (r: DemographicRow) =>
    (Number(r.customers) / (totals.get(r.period) || 1)) * 100;
  const columns: DashboardTableColumn<DemographicRow>[] = [
    {
      id: "period",
      label: periodLabel(granularity),
      value: (r) => r.period,
    },
    {
      id: "segment",
      label: REFERRAL_DIMENSIONS[dimension as keyof typeof REFERRAL_DIMENSIONS],
      value: (r) => r.segment,
    },
    {
      id: "customers",
      label: "Personer",
      value: (r) => r.customers,
      numeric: true,
      align: "right",
    },
    {
      id: "share",
      label: "Andel (%)",
      value: share,
      numeric: true,
      align: "right",
    },
  ];
  const table = useDashboardTable(`demographics-${role}`, columns, rows);
  const exportCsv = () =>
    downloadCsv(
      toCsv(
        ["Periode", "Rolle", "Fordeling", "Gruppe", "Personer", "Andel (%)"],
        (mode === "table" ? table.viewRows : rows).map((r) => [
          r.period,
          role === "referrer" ? "Verver" : "Vervet",
          dimension,
          r.segment,
          r.customers,
          share(r).toFixed(2).replace(".", ","),
        ]),
        ";",
        { neutralizeFormulas: true },
      ),
      `verving-demografi-${role}-${dimension}-${from}-${to}.csv`,
      { bom: true },
    );
  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-end justify-between gap-3 space-y-0">
        <CardTitle>{role === "referrer" ? "Ververe" : "Vervede"}</CardTitle>
        <Button
          size="sm"
          variant="outline"
          onClick={exportCsv}
          disabled={!rows.length}
        >
          <Download className="h-3 w-3" />
          Eksporter CSV
        </Button>
        <div className="flex w-full flex-wrap gap-2">
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Fordeling</span>
            <Select value={dimension} onChange={(e) => update({ [dimensionKey]: e.target.value })}>
              {Object.entries(REFERRAL_DIMENSIONS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </Select>
          </label>
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Måltall</span>
            <Select value={view} onChange={(e) => update({ [viewKey]: e.target.value })}><option value="absolute">Antall personer</option><option value="percent">Andel (%)</option></Select>
          </label>
          <label className="space-y-1"><span className="text-xs text-admin-text-subtle">Visning</span>
            <Select value={mode} onChange={(e) => update({ [modeKey]: e.target.value })}><option value="chart">Graf</option><option value="table">Tabell</option></Select>
          </label>
        </div>
      </CardHeader>
      <CardContent>
        {!rows.length ? (
          <p className="py-8 text-sm text-admin-text-subtle">
            Ingen vervinger i perioden.
          </p>
        ) : mode === "table" ? (
          <DashboardTable
            table={table}
            columns={columns}
            renderRow={(r) => (
              <TableRow key={JSON.stringify([r.period, r.segment])}>
                <TableCell>{r.period}</TableCell>
                <TableCell>{r.segment}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {r.customers}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {share(r).toLocaleString("nb-NO", {
                    maximumFractionDigits: 1,
                  })}{" "}
                  %
                </TableCell>
              </TableRow>
            )}
          />
        ) : (
          <div className="overflow-x-auto"><div style={{ minWidth: data.length * 46 }}>
          <ResponsiveContainer width="100%" height={340}>
            <ComposedChart data={data.map((r) => ({ ...r, stackHeight: percent ? Number(r.total) ? 100 : 0 : r.total }))} margin={{ top: 26, right: 12 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--color-admin-card-border)"
              />
              <XAxis
                dataKey="period"
                tick={{ fontSize: 11, fill: "var(--color-admin-text-subtle)" }}
              />
              <YAxis
                domain={percent ? [0, 100] : [0, "auto"]}
                allowDecimals={percent}
                tickFormatter={(v) => `${v}${percent ? " %" : ""}`}
                tick={{ fontSize: 11, fill: "var(--color-admin-text-subtle)" }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "var(--color-admin-card)",
                  borderColor: "var(--color-admin-card-border)",
                  color: "var(--color-admin-text-primary)",
                }}
                formatter={(value, _name, item) => {
                  const total = Number(item.payload.total);
                  const count = percent
                    ? Math.round((Number(value) * total) / 100)
                    : Number(value);
                  const percentage = total ? (count / total) * 100 : 0;
                  return `${count.toLocaleString("nb-NO")} personer (${percentage.toLocaleString("nb-NO", { maximumFractionDigits: 1 })} %)`;
                }}
                labelFormatter={(label, payload) =>
                  `${label} · ${payload?.[0]?.payload?.total ?? 0} personer`
                }
              />
              <Legend itemSorter={null} wrapperStyle={{ fontSize: 11 }} />
              {segments.map((segment, i) => (
                <Bar
                  key={segment}
                  dataKey={`s${i}`}
                  name={segment}
                  stackId="people"
                  fill={referralCategoryColor(dimension, segment)}
                  isAnimationActive={false}
                >
                  <LabelList dataKey={`s${i}`} position="center" fill="var(--color-admin-text-primary)" fontSize={10}
                    style={{ paintOrder: "stroke", stroke: "var(--color-admin-card)", strokeWidth: 2 }}
                    formatter={(v) => Number(v) > 0 ? `${Number(v).toLocaleString("nb-NO", { maximumFractionDigits: 0 })}${percent ? " %" : ""}` : ""} />
                </Bar>
              ))}
              <Line dataKey="stackHeight" stroke="none" dot={false} activeDot={false} legendType="none" tooltipType="none" isAnimationActive={false}>
                <LabelList dataKey="total" position="top" fill="var(--color-admin-text-primary)" fontSize={11} formatter={(v) => Number(v) > 0 ? percent ? `n=${v}` : String(v) : ""} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
          </div></div>
        )}
      </CardContent>
    </Card>
  );
}
